import { useState } from 'react'
import type { GlossarySystemKey } from '@ai-meeting/shared'
import SidebarToggle from '@/components/SidebarToggle'
import { DocumentIcon, UploadIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import { ApiError } from '@/services/api'
import { logActivity } from '@/services/activity'
import type { UserActivityAction } from '@ai-meeting/shared'
import SystemDictionaryRow from './components/SystemDictionaryRow'
import UserDictionaryRow from './components/UserDictionaryRow'
import UploadDictionaryModal from './components/UploadDictionaryModal'
import { useGlossaryDictionaries } from './useGlossaryDictionaries'

export default function GlossaryPage() {
  const { t } = useUiStrings()
  const glossary = useGlossaryDictionaries()
  const [uploadOpen, setUploadOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null)
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null)

  function clearDragState() {
    setDragFromIndex(null)
    setDropTargetIndex(null)
  }

  /**
   * Run one dictionary action, and count it either way (BRAI-677).
   *
   * Every button on this page already funnels through here, so this is the one
   * place that knows both that something was pressed and how it turned out —
   * which is what the report needs. Counting only what worked would make a
   * dictionary nobody can upload read as a dictionary nobody wants.
   *
   * @param activity Which feature was used, for the per-function report.
   */
  async function runAction(activity: UserActivityAction, action: () => Promise<void>) {
    setActionError(null)
    try {
      await action()
      logActivity(activity)
    } catch (err) {
      logActivity(activity, { status: 'failure' })
      setActionError(err instanceof ApiError ? err.message : t.glossaryActionFailed)
    }
  }

  return (
    <>
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 sm:px-6">
        <SidebarToggle />
        <h1 className="min-w-0 flex-1 truncate text-lg font-bold text-[var(--text-primary)]">
          {t.glossary}
        </h1>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
        {(glossary.error || actionError) && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs font-medium text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            {actionError || glossary.error}
          </div>
        )}

        <section>
          <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-tertiary)]">
            {t.glossarySystemDictionaries}
          </h2>
          <div className="mt-3 space-y-3">
            {glossary.loading && (
              <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-6 text-sm text-[var(--text-tertiary)]">
                {t.loading}
              </div>
            )}
            {!glossary.loading &&
              glossary.system
                .filter((dictionary) => dictionary.termCount > 0)
                .map((dictionary) => (
                  <SystemDictionaryRow
                    key={dictionary.id}
                    dictionary={dictionary}
                    onDownload={() =>
                      void runAction('glossary.downloaded', async () => {
                        const key = dictionary.systemKey as GlossarySystemKey
                        await glossary.downloadSystem(key, dictionary.name)
                      })
                    }
                  />
                ))}
          </div>
        </section>

        <section className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-tertiary)]">
              {t.glossaryYourDictionaries}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  void runAction('glossary.downloaded', async () => {
                    await glossary.downloadTemplate()
                  })
                }
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 text-xs font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)]"
              >
                <DocumentIcon className="h-3.5 w-3.5" />
                {t.glossaryDownloadTemplate}
              </button>
              <button
                type="button"
                onClick={() => setUploadOpen(true)}
                className="btn-primary inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold"
              >
                <UploadIcon className="h-3.5 w-3.5" />
                {t.glossaryUploadDictionary}
              </button>
            </div>
          </div>

          <p className="mt-2 text-xs text-[var(--text-tertiary)]">{t.glossaryReorderHint}</p>

          <div className="mt-3 space-y-3">
            {!glossary.loading && glossary.user.length === 0 && (
              <div className="rounded-lg border border-dashed border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
                {t.glossaryEmptyUser}
              </div>
            )}
            {glossary.user.map((dictionary, index) => (
              <UserDictionaryRow
                key={dictionary.id}
                dictionary={dictionary}
                index={index}
                isFirst={index === 0}
                isLast={index === glossary.user.length - 1}
                isDragging={dragFromIndex === index}
                isDropTarget={dropTargetIndex === index && dragFromIndex !== index}
                onRename={(name) => glossary.rename(dictionary.id, name)}
                onSetDefault={(isDefault) =>
                  runAction('glossary.updated', () => glossary.setDefault(dictionary.id, isDefault))
                }
                onMoveUp={() =>
                  void runAction('glossary.updated', () => glossary.move(dictionary.id, 'up'))
                }
                onMoveDown={() =>
                  void runAction('glossary.updated', () => glossary.move(dictionary.id, 'down'))
                }
                onDownload={() =>
                  void runAction('glossary.downloaded', async () => {
                    await glossary.downloadUser(dictionary.id, dictionary.name)
                  })
                }
                onDelete={() => {
                  if (!window.confirm(t.glossaryDeleteConfirm)) return
                  void runAction('glossary.deleted', () => glossary.remove(dictionary.id))
                }}
                onDragStart={() => {
                  setDragFromIndex(index)
                  setDropTargetIndex(null)
                }}
                onDragEnd={clearDragState}
                onDragOver={(event) => {
                  event.preventDefault()
                  event.dataTransfer.dropEffect = 'move'
                  if (dragFromIndex != null && dragFromIndex !== index) {
                    setDropTargetIndex(index)
                  }
                }}
                onDrop={() => {
                  const from = dragFromIndex
                  clearDragState()
                  if (from == null) return
                  void runAction('glossary.updated', () => glossary.reorder(from, index))
                }}
              />
            ))}
          </div>
        </section>
      </div>

      {uploadOpen && (
        <UploadDictionaryModal
          uploading={glossary.mutating}
          onClose={() => setUploadOpen(false)}
          onUpload={glossary.uploadOne}
        />
      )}
    </>
  )
}
